/* eslint eqeqeq:0 */
export default angular.module('eventix.common.services.seatsio', [])
    .factory('SeatsIO', SeatsIO).name;

function SeatsIO(OrderManager, $q, UIMessages, $timeout, store, LibraryLoader, $filter) {
    let sessionInfo = store.get('seatsTokens');
    if (!sessionInfo || Date.now() - sessionInfo.timestamp > 25 * 60 * 1000) {
        /* If token is older than 25 minutes, delete it */
        store.remove('seatsTokens');
        sessionInfo = {
            timestamp: Date.now()
        };
    }
    /**
     * @ngdoc service
     * @name eventix.common.services.SeatsIO
     * @description
     * A service for SeatsIO integration.
     */
    const service = {
        /**
         * @ngdoc method
         * @name eventix.common.services.SeatsIO#open
         * @methodOf eventix.common.services.SeatsIO
         * @description Open seat selection
         * @returns {Promise} Resolves when loaded
         */
        load() {
            return LibraryLoader.load('https://cdn.seatsio.net/chart.js');
        },
        /**
         * SeatsIO Session info (token, expiration info)
         */
        sessionInfo: sessionInfo,
        /**
         * A dynamic reference to the current order
         */
        get order() { return OrderManager.instance; },
        /**
         * A reference to the currently rendered SeatsIO chart
         */
        chart: null,
        /**
         * A reference to the currently selected ticket
         */
        ticket: null,
        /**
         * A reference to the currently selected event GUID
         */
        eventId: null,
        /**
         * Whether or not the user can continue after selecting seats
         * @type {boolean}
         */
        canContinueAfterSelectingSeats: true,
        /**
         * All violations done in the Seats UI 
         * @type {Array<string>} the list of violations e.g.: 'noOrphanSeats'
         */
        violations: [],
        /**
         * @ngdoc method
         * @name eventix.common.services.SeatsIO#open
         * @methodOf eventix.common.services.SeatsIO
         * @description Open seat selection
         * @param {Ticket} ticket The selected ticket
         * @param {String} eventId The GUID of the event this ticket belongs to
         * @param {String} divId The ID of the DOM element where the chart should be rendered
         * @returns {Promise} Resolves when rendered
         */
        open(ticket, eventId, divId = 'seatSelect') {
            service.ticket = ticket;
            service.eventId = eventId;
            service.canContinueAfterSelectingSeats = true
            service.violations = []
            const products = ticket.belongsToMany.restore('Product');
            const eventDates = _.flatMap(products, p => p.belongsToMany.restore('EventDate'));
            /* Find all seats event keys. Each key represents a seating plan. */
            service.eventKeys = _.uniq(_.compact(_.map(eventDates, 'seats_event_key')));
            if (service.eventKeys.length < 1) {
                UIMessages.push('Cannot find seatmap');
                return $q.reject(new Error('Cannot find seatmap'));
            }

            return $timeout(() => {
                let opts = {
                    divId: 'seatSelect',
                    publicKey: service.order.shop.seats_public_key,
                    holdOnSelect: true,
                    holdTokenInputName: 'resTokenInput',
                    showRowLines: true,
                    showLegend: true,
                    legend: {
                        hideNonSelectableCategories: true
                    },
                    selectionValidators: [
                        {
                            type: 'noOrphanSeats', highlight: !_.get(service.order.shop, 'seats_allow_orphan_computed', false)
                        }
                    ],
                    events: service.eventKeys,
                    extraConfig: { tickets: service.order.tickets[eventId] },
                    pricing: service.buildPricings(eventId),
                    priceFormatter: function(price) {
                        return $filter('formatCurrency')(price);
                    },
                    onSelectionValid: service.onSelectionValid,
                    onSelectionInvalid: service.onSelectionInvalid,
                    // unavailableCategories: [], // TODO: either list all categories in the back-end and retrieve them here or retrieve all categories by rerendering the chart after it has already been rendered.
                    isObjectSelectable: service.isObjectSelectable,
                    onObjectSelected: service.onObjectSelected,
                    onObjectDeselected: service.onObjectDeselected,
                };

                if (!service.sessionInfo.token) {
                    /* User opens seating plan for the first time. Make sure we generate a token and store that in localStorage */
                    opts.regenerateHoldToken = true;
                    opts.onChartRendered = function() {
                        service.order.seatsReservationToken = service.chart.holdToken;
                        service.sessionInfo.token = service.chart.holdToken;
                        store.set('seatsTokens', service.sessionInfo);
                    };
                } else {
                    /* Reuse existing token, ensures we can reopen the same seating chart multiple times and thus edit our reservations. */
                    opts.holdToken = service.sessionInfo.token;
                    service.order.seatsReservationToken = service.sessionInfo.token;

                    // Restore general addmission numbers on chart rendering
                    opts.onChartRendered = function(chart) {
                        _.each(service.order.reservations, function(tickets){
                            _.each(tickets, function(reservation) {
                                if (_.get(reservation, 'seatInfo.GeneralAdmissionArea', false) === true) {
                                    chart.selectObjects([{
                                        label: _.get(reservation, 'seatInfo.uuid'),
                                        ticketType: _.get(reservation, 'ticket.name'),
                                        amount: 1
                                    }]);
                                }
                            });
                        });
                    };
                }
                service.chart = new window.seatsio.SeatingChart(opts).render()
            });
        },
        /**
         * Generate a list of pricings for SeatsIO
         * @param {String} eventId EventGUID for which to generate pricings
         * @returns {Array<Object>} Array of prices `{ticketType, price}`
         */
        buildPricings(eventId) {
            const pricings = [];
            let ticketsByCategory = _.groupBy(service.order.tickets[eventId], 'seats_category_key');
            _.forEach(ticketsByCategory, function(tickets, categoryKey) {
                tickets = _.filter(tickets, function(ticket) {
                    return !ticket.comingSoon && !ticket.recentlyExpired && ticket.status === 'available';
                });
                if (tickets.length === 1)
                    pricings.push({ 'category': categoryKey, 'price': tickets[0].minimumPrice });
                else {
                    let ticketTypes = [];
                    tickets = _.sortBy(tickets, 'minimumPrice');
                    _.forEach(tickets, function(ticket) {
                        ticketTypes.push({ ticketType: ticket.name, price: ticket.minimumPrice });
                    });
                    pricings.push({ 'category': categoryKey, 'ticketTypes': ticketTypes });
                }
            });
            return pricings;
        },
        /**
         * TODO: deprecated in seats v2, instead of this callback use the property `unavailableCategories : Array<number>`
         * Check if the selected object is available
         * @param {Object} object Selection object
         * @param {Boolean} defaultValue SeatsIO default selectability
         * @param {Object} extraConfig Passthrough of seatsOpts,extraConfig, containing a reference to this service
         * @returns {Boolean} True if the seat is available for purchase
         */
        isObjectSelectable(object, defaultValue, extraConfig) {
            let ticket;
            extraConfig.tickets.forEach(function(t) {
                let isAvailable = !t.comingSoon && !t.recentlyExpired && t.status === 'available';
                let categoryMatches = t.seats_category_key &&
                    object && object.category &&
                    t.seats_category_key == object.category.key;
                if (isAvailable && categoryMatches)
                    ticket = t;
            });
            if (!ticket)
                return false;
            return defaultValue;
        },
        /**
         * Callback when user selects a seat - Reserves a ticket with the API
         * @param {Object} object Reservation object from seats
         * @param {*} selectedTicketType TicketType information from seats
         * @returns {Promise} Resolves when selected
         */
        onObjectSelected(object, selectedTicketType) {
            // If objects (GA) are selected from onChartRendered we want to skip the reservation logic
            // Because we are restoring the chart from the reservations and Seats.io does not take care of GA's
            // price seems to be ommited on selectObjects function
            if(selectedTicketType && _.get(selectedTicketType,'price') == null)
                return;

            const ticket = _.find(service.order.tickets[service.eventId], ticket => {
                const isAvailable = !ticket.comingSoon &&
                    !ticket.recentlyExpired &&
                    ticket.status === 'available';
                const categoryMatches = ticket.seats_category_key &&
                    ticket.seats_category_key == _.get(object, 'category.key');
                const ticketMatches = !selectedTicketType || (
                    ticket.name === selectedTicketType.ticketType &&
                    ticket.minimumPrice === selectedTicketType.price
                );
                return isAvailable && categoryMatches && ticketMatches;
            });
            if (!ticket) {
                UIMessages.push('Ticket not found');
                service.chart.deselectObjects([object.label]);
                return $q.reject();
            }

            // Find if there are already reservations with these combinations
            // If the seatChart is opened multiple times, we don't want to select the same seat twice
            const reservedSeats = _.find(
                service.order.reservations[ticket.guid],
                reservation => _.get(reservation, 'seatInfo.uuid') === object.label
            );
            let isGeneralAdmissionArea = _.get(object, 'objectType') == 'GeneralAdmissionArea';
            if (object.objectType != 'GeneralAdmissionArea' && reservedSeats)
                return $q.reject();

            return service.order.reserveTicket(ticket.guid, service.eventId).then(() => {
                const reservation = _.last(service.order.reservations[ticket.guid]);
                reservation.seatInfo = {
                    eventKeys: service.eventKeys,
                    uuid: object.label,
                    GeneralAdmissionArea: isGeneralAdmissionArea,
                    reservationToken: service.sessionInfo.token,
                    publicKey: service.order.shop.seats_public_key
                };
            }, error => {
                UIMessages.push('Seat could not be reserved');
                service.chart.deselectObjects([object.label]);
                return $q.reject(error);
            });
        },
        /**
         * Callback when user selects a seat - Releases a reservation
         * @param {Object} object Reservation object from seats
         * @param {*} selectedTicketType TicketType information from seats
         * @returns {Promise} Resolves when selected
         */
        onObjectDeselected(object, selectedTicketType) {
            const ticket = _.find(service.order.tickets[service.eventId], ticket => {
                const categoryMatches = ticket.seats_category_key &&
                    ticket.seats_category_key == _.get(object, 'category.key');
                const ticketMatches = !selectedTicketType || (
                    ticket.name === selectedTicketType.ticketType &&
                    ticket.minimumPrice === selectedTicketType.price
                );
                return categoryMatches && ticketMatches;
            });
            if (!ticket) {
                UIMessages.push('Ticket not found');
                return $q.reject(new Error('Ticket not found'));
            }

            const reservation = _.find(
                service.order.reservations[ticket.guid],
                reservation => _.get(reservation, 'seatInfo.uuid') === object.label
            );
            if (!reservation) {
                UIMessages.push('Reservation not found');
                return $q.reject(new Error('Reservation not found'));
            }

            return service.order.releaseTicket(reservation, service.eventId);
        },
        /**
         * Whenever the selection of seats is valid
         */
        onSelectionValid: () => {
            service.canContinueAfterSelectingSeats = true
            service.violations = []
        },
        /**
         * Whenever the selection of seats is invalid
         * @param {Array<string>} violations a list containing all violations as a string.
         */
        onSelectionInvalid: (violations) => {
            if(_.includes(violations, 'noOrphanSeats') && _.get(service.order.shop, 'seats_allow_orphan_computed', false) === false) {
                service.canContinueAfterSelectingSeats = false
                service.violations = violations
            }
            else if (!_.includes(violations, 'noOrphanSeats')){
                service.canContinueAfterSelectingSeats = false
                service.violations = violations
            }
        }
    };
    return service;
}